// ============================================================================
// ClientMealLog — the client's "log a meal" experience (amber / nutrition).
// ----------------------------------------------------------------------------
// One scrollable column (mobile-first):
//   1. Daily targets card (progress vs logged intake + non-medical disclaimer)
//   2. Water tracker (quick-add buttons + today total vs target)
//   3. Meal form: meal-type, title, FULL macros (kcal/protein/carbs/fat),
//      optional note, optional photo (≤2 MB) — posts to /me/meals then, if a
//      photo was chosen, uploads it to /me/meals/{id}/photo.
//   4. Today's meals list with per-meal macros + a running daily total, each
//      row showing its thumbnail (decrypt-on-read) and a delete action.
// Optional-first: blank fields are dropped; a meal needs at least a title or a
// macro. Writes invalidate the nutrition + dashboard views. Pulse, EN + 繁中.
// ============================================================================
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardHeader,
  DateField,
  SelectField,
  TextField,
} from "../../components";
import { clientApi } from "../../api";
import type { Meal, MealInput, MealType } from "../../api";
import {
  useClientMeals,
  useDeleteClientMeal,
  useLogClientMeal,
  useUploadMealPhoto,
  todayDay,
} from "../hooks";
import { fmtNum } from "./portalLib";

const MEAL_PHOTO_MAX_BYTES = 2 * 1024 * 1024; // mirror the server's 2 MB cap.

const MEAL_TYPES: MealType[] = ["breakfast", "lunch", "dinner", "snack"];

function numOrUndef(s: string): number | undefined {
  const v = s.trim();
  if (v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

/** ISO date ("YYYY-MM-DD") -> a local-midnight instant the API normalizes to UTC. */
function dayToIso(day: string): string | undefined {
  const s = day.trim();
  if (!s) return undefined;
  const d = new Date(`${s}T12:00`); // midday avoids day-shift across timezones
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export function ClientMealLog({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation("nutrition");
  const log = useLogClientMeal();
  const uploadPhoto = useUploadMealPhoto();
  const fileRef = useRef<HTMLInputElement>(null);

  const [meal, setMeal] = useState<MealType | "">("");
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState(todayDay());
  const [kcal, setKcal] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [needSomething, setNeedSomething] = useState(false);

  const reset = () => {
    setMeal("");
    setTitle("");
    setWhen(todayDay());
    setKcal("");
    setProtein("");
    setCarbs("");
    setFat("");
    setNotes("");
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhotoError(false);
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MEAL_PHOTO_MAX_BYTES) {
      setPhotoError(true);
      setPhoto(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setPhoto(f);
  };

  const submit = () => {
    setSaved(false);
    setNeedSomething(false);
    const body: MealInput = {};
    if (meal) body.meal = meal;
    if (title.trim()) body.title = title.trim();
    const eaten = dayToIso(when);
    if (eaten) body.eaten_at = eaten;
    const kcalN = numOrUndef(kcal);
    if (kcalN !== undefined) body.kcal = kcalN;
    const proteinN = numOrUndef(protein);
    if (proteinN !== undefined) body.protein_g = proteinN;
    const carbsN = numOrUndef(carbs);
    if (carbsN !== undefined) body.carbs_g = carbsN;
    const fatN = numOrUndef(fat);
    if (fatN !== undefined) body.fat_g = fatN;
    if (notes.trim()) body.notes = notes.trim();

    // A meal needs at least a title or one macro figure to be meaningful.
    const hasMacro =
      body.kcal != null ||
      body.protein_g != null ||
      body.carbs_g != null ||
      body.fat_g != null;
    if (!body.title && !hasMacro) {
      setNeedSomething(true);
      return;
    }

    log.mutate(body, {
      onSuccess: (created) => {
        const file = photo;
        const finish = () => {
          setSaved(true);
          reset();
          if (onDone) window.setTimeout(onDone, 900);
        };
        if (file && created?.id != null) {
          uploadPhoto.mutate(
            { mealId: created.id, file },
            { onSettled: finish },
          );
        } else {
          finish();
        }
      },
    });
  };

  const typeOptions = MEAL_TYPES.map((m) => ({
    value: m,
    label: t(`meal.${m}`),
  }));

  const busy = log.isPending || uploadPhoto.isPending;

  return (
    <div className="space-y-5">
      <Card className="space-y-6">
        <div>
          <h2 className="font-display text-lg font-bold text-body">{t("logMeal")}</h2>
        </div>

        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <SelectField
            label={t("meal.type")}
            placeholder={t("meal.typePlaceholder")}
            value={meal}
            onChange={(e) => setMeal(e.target.value as MealType)}
            options={typeOptions}
          />

          <TextField
            label={t("meal.name")}
            placeholder={t("meal.namePlaceholder")}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (needSomething) setNeedSomething(false);
            }}
          />

          <DateField label={t("meal.eatenAt")} value={when} onChange={setWhen} />

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={t("macro.kcal")}
              value={kcal}
              onChange={(e) => {
                setKcal(e.target.value);
                if (needSomething) setNeedSomething(false);
              }}
              type="number"
              inputMode="numeric"
              min={0}
              suffix={t("macro.kcalUnit")}
            />
            <TextField
              label={t("macro.protein")}
              value={protein}
              onChange={(e) => {
                setProtein(e.target.value);
                if (needSomething) setNeedSomething(false);
              }}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0}
              suffix={t("macro.gUnit")}
            />
            <TextField
              label={t("macro.carbs")}
              value={carbs}
              onChange={(e) => {
                setCarbs(e.target.value);
                if (needSomething) setNeedSomething(false);
              }}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0}
              suffix={t("macro.gUnit")}
            />
            <TextField
              label={t("macro.fat")}
              value={fat}
              onChange={(e) => {
                setFat(e.target.value);
                if (needSomething) setNeedSomething(false);
              }}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0}
              suffix={t("macro.gUnit")}
            />
          </div>

          <div className="w-full">
            <label
              htmlFor="vf-meal-notes"
              className="mb-1.5 block font-display text-sm font-bold text-body"
            >
              {t("meal.notes")}
            </label>
            <textarea
              id="vf-meal-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t("meal.notesPlaceholder")}
              className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
            />
          </div>

          <div className="w-full">
            <span className="mb-1.5 block font-display text-sm font-bold text-body">
              {t("photo")}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onPickPhoto}
              className="block w-full text-sm text-body-soft file:mr-3 file:rounded-pill file:border-0 file:bg-amber-soft file:px-4 file:py-2 file:font-display file:text-sm file:font-bold file:text-amber-600 hover:file:bg-amber/15"
            />
            {photo && (
              <p className="mt-1.5 text-sm text-body-muted">{photo.name}</p>
            )}
          </div>

          {needSomething && <Banner tone="warning" title={t("needTitle")} />}
          {photoError && <Banner tone="warning" title={t("photoTooLarge")} />}
          {log.isError && (
            <Banner tone="danger" title={t("saveError")}>
              {t("saveErrorBlurb")}
            </Banner>
          )}
          {saved && <Banner tone="success" title={t("saved")} />}

          <Button type="submit" block size="lg" disabled={busy}>
            {busy ? t("saving") : t("save")}
          </Button>
        </form>
      </Card>

      <TodaysMeals />
    </div>
  );
}

// --- today's meals + running daily total -----------------------------------

function TodaysMeals() {
  const { t } = useTranslation("nutrition");
  const meals = useClientMeals(todayDay());
  const del = useDeleteClientMeal();

  if (meals.isLoading) {
    return (
      <Card>
        <CardHeader title={t("todaysMeals")} />
        <div className="space-y-2" aria-hidden>
          {[0, 1].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-md bg-surface-sunken" />
          ))}
        </div>
      </Card>
    );
  }

  const rows = meals.data?.meals ?? [];
  const totals = meals.data?.totals;

  return (
    <Card className="space-y-4">
      <CardHeader title={t("todaysMeals")} />

      {rows.length === 0 ? (
        <p className="rounded-md bg-surface-sunken px-4 py-6 text-center text-sm text-body-soft">
          {t("noMeals")}
        </p>
      ) : (
        <>
          <ul className="space-y-2">
            {rows.map((m) => (
              <MealRow
                key={m.id}
                meal={m}
                onDelete={() => {
                  if (window.confirm(t("deleteMealConfirm"))) del.mutate(m.id);
                }}
                deleting={del.isPending && del.variables === m.id}
              />
            ))}
          </ul>

          {totals && (
            <div className="flex items-center justify-between rounded-lg bg-grad-amber px-4 py-3 text-white shadow-sm">
              <span className="font-display text-sm font-bold">{t("dailyTotal")}</span>
              <span className="text-sm font-bold tabular-nums">
                {fmtNum(totals.kcal, 0)} {t("macro.kcalUnit")} · P{fmtNum(totals.protein_g, 0)} ·
                C{fmtNum(totals.carbs_g, 0)} · F{fmtNum(totals.fat_g, 0)} {t("macro.gUnit")}
              </span>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function MealRow({
  meal,
  onDelete,
  deleting,
}: {
  meal: Meal;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation("nutrition");
  const macros: string[] = [];
  if (meal.kcal != null) macros.push(`${fmtNum(meal.kcal, 0)} ${t("macro.kcalUnit")}`);
  if (meal.protein_g != null) macros.push(`P${fmtNum(meal.protein_g, 0)}`);
  if (meal.carbs_g != null) macros.push(`C${fmtNum(meal.carbs_g, 0)}`);
  if (meal.fat_g != null) macros.push(`F${fmtNum(meal.fat_g, 0)}`);

  return (
    <li className="flex items-center gap-3 rounded-md bg-surface-sunken px-3 py-3">
      {meal.photo_id != null ? (
        <img
          src={clientApi.mealPhotoUrl(meal.id)}
          alt={meal.title ?? t("photo")}
          loading="lazy"
          className="h-12 w-12 shrink-0 rounded-md object-cover ring-1 ring-line"
        />
      ) : (
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-amber-soft text-amber-600">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
            <path
              d="M5 19V8l3-3h8l3 3v11z M9 5V3h6v2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {meal.meal && (
            <Badge tone="warning">{t(`meal.${meal.meal}`, { defaultValue: meal.meal })}</Badge>
          )}
          <span className="truncate font-display font-bold text-body">
            {meal.title || t("meals")}
          </span>
        </div>
        {macros.length > 0 && (
          <p className="mt-0.5 truncate text-sm tabular-nums text-body-soft">
            {macros.join(" · ")}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        aria-label={t("deleteMeal")}
        className="shrink-0 rounded-pill p-2 text-body-faint transition-colors hover:bg-danger/10 hover:text-danger disabled:opacity-50"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M5 7h14M9 7V5h6v2M7 7l1 12h8l1-12"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </li>
  );
}
